import React, { Component, Fragment } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LoadingSpinnerSVG, Logo, LanguagePicker } from 'cx-ui-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { mappedLocalesOptions } from '../../i18n';
import { cxInit, versionCheck } from '../../utils/cxSdk';
import { cxSubmitUserAccount, cxGetUserEmail, cxSetTenant } from '../../utils/cxSdk';
import { customSortStringMethod, setTenantOptionStyle, requiresReAuth } from "../../utils/index";
import { Toast } from 'cx-ui-components';
import { isEmpty } from 'serenova-js-utils/strings';

import PrivacyPolicy from '../Legal/privacyPolicy';
import LegalPolicy from '../Legal/legalPolicy';
import messages from './messages';

const Loading = styled(LoadingSpinnerSVG)`
  top: 150px;
  top: calc(50% - ${(props) => props.size / 2}px);
  left: calc(50% - ${(props) => props.size / 2}px);
`;

const TempBackground = styled.div`
  position: fixed;
  top: 0px;
  width: 100%;
  height: 100%;
  background-repeat: repeat;
  background-image: url('https://qe-config.cxengagelabs.net/assets/images/square_bg.png'),
    linear-gradient(to bottom, #0a64aa, #3498db);
  z-index: -1;
`;

const LoginFormWrapper = styled.div`
  position: relative;
  top: 70px;
`;

const LoginBox = styled.div`
  width: 540px;
  height: 560px;
  margin: 0px auto;
  margin-top: calc(50vh - 270px);
  border-radius: 3px;
  background-color: rgb(255, 255, 255);
  box-shadow: rgba(0, 0, 0, 0.09) 0px 0px 6px 0px;
  position: relative;
  z-index: 1;
`;

const TextInput = styled.input`
  width: 282px;
  height: 44px;
  background-color: rgb(255, 255, 255);
  font-size: 16px;
  font-style: normal;
  font-stretch: normal;
  color: rgb(73, 73, 73);
  outline: none;
  padding: 0px 11px;
  border-radius: 2px;
  border-width: 1px;
  border-style: solid;
  border-color: rgb(151, 151, 151);
  margin-bottom: 10px;
  display: block;
  margin: 10px auto;
`;

const SubmitButton = styled.button`
  border-radius: 8px;
  border-width: 0px;
  background-color: rgb(35, 205, 244);
  font-size: 16px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  color: rgb(255, 255, 255);
  padding: 14px 28px;
  cursor: pointer;
  outline: none;
  display: block;
  margin: 10px auto;
`;

const StyledLanguagePicker = styled(LanguagePicker)`
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 1.5em;
`;

const UserNameLabel = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  color: rgb(73, 73, 73);
  text-align: center;
  padding-bottom: 23px;
`;

const storage = window.localStorage;

const styles = {
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 'auto',
  },
};

export class InviteAccept extends Component {
  constructor() {
    super();
    this.state = {
      confirmPassword: '',
      firstName: '',
      lastName: '',
      loading: false,
      newPassword: '',
      redirect: false,
      sdkLoaded: false,
    };
  }

  componentDidMount() {
    cxInit();

    // validate if page has loaded completely
    addEventListener('load', this.handleLoadEvent);

    if (this.props.getUserEmail && this.props.getUserEmail === 'no-userEmail') {
      this.setState({ redirect: true });
    }
  }

  componentWillUnmount() {
    clearInterval(versionCheck);
    removeEventListener('load', this.handleLoadEvent);
    window.removeEventListener('message', this.handleMessageEvent);
  }

  componentDidUpdate(prevProps) {
    if ((this.props.getUserEmail && this.props.getUserEmail === 'no-userEmail') ||
      (prevProps.getUserEmail === 'no-userEmail' && this.props.getUserEmail === 'no-userEmail')) {
      this.setState({ redirect: true });
    }
  }

  handleLoadEvent = () => {
    // constantly check if sdk has finished initialization
    window.addEventListener('message', this.handleMessageEvent);
  };

  handleMessageEvent = () => {
    if (CxEngage.version && !this.state.sdkLoaded) {
      this.setState({ sdkLoaded: true });

      if (CxEngage.version) {
        cxGetUserEmail();
      }
    }

    if (CxEngage.version && this.state.sdkLoaded) {
      if (this.props.getUserEmail && this.props.getUserEmail === 'no-userEmail') {
        this.setState({ redirect: true });
      }
    }
  };

  handleLoginResponse = (response) => {
    if (response.code && (response.code === 3002 || response.code === 3000)) {
      const errorMessage = response.message ? response.message : this.props.intl.formatMessage(messages.errorUnexpected);
      Toast.error(errorMessage);
    }

    let defaultTenantObject;
    const { defaultTenant, tenants, platformPermissions, userId } = response;
    this.props.setAccountTenants(tenants);
    // Users do not have a 'default tenant' by default
    if (defaultTenant) {
      defaultTenantObject = response.tenants.filter((tenant) => tenant.tenantId === response.defaultTenant)[0];
    }
    if (!defaultTenantObject) {
      defaultTenantObject = response.tenants[0];
    }
    this.setState({ tenantId: defaultTenantObject.tenantId });

    let self = this;
    CxEngage.session.getTenantDetails((sessionError, sessionTopic, sessionResponse) => {
      if (sessionError) {
        const errorMessage = sessionError.message ? sessionError.message : self.props.intl.formatMessage(messages.errorUnexpected);
        Toast.error(errorMessage);
        self.setState({ loading: false });
      } else {
        switch (sessionTopic) {
          case 'cxengage/session/get-tenant-details': {
            if (sessionResponse) {
              sessionResponse.details.sort((a, b) => {
                return customSortStringMethod(a.name, b.name);
              });
              if (sessionResponse.details.length) {
                self.props.setUserTenants(sessionResponse.details);
                // get everything but the selected item
                const tenantOptions = sessionResponse.details
                  .filter((tenant) => tenant.active)
                  .map((tenant) => ({
                    value: tenant.tenantId,
                    label: tenant.name,
                    style: setTenantOptionStyle(false, tenant, self.props.availableTenants, self.props.accountTenants),
                    className: 'account-tenant',
                    requiresReAuth: false,
                    title: '',
                  }));
                self.props.setTenantsList(tenantOptions);
              }
            }
            break;
          }
          default: {
            // Do Nothing
            break;
          }
        }
      }
    });

    sessionStorage.setItem('token', CxEngage.dumpState().authentication.token);
    sessionStorage.setItem('defaultTenant', defaultTenant);

    this.props.updateUserPermissions(
      defaultTenantObject.tenantId,
      defaultTenantObject.tenantName,
      defaultTenantObject.tenantPermissions,
      userId
    );
    this.props.updatePlatformPermissions(platformPermissions);
    this.props.setUserAuthed(true);

    cxSetTenant(defaultTenantObject.tenantId, (response) => {
      if (response && response.tenantId) {
        this.props.switchTenant(response.tenantId);
        this.props.fetchBranding();
      }
    });
    this.setState({ loading: false });
  };

  submitUserAccount = () => {
    //if validate password
    // Check length
    // Must be at least 8 characters in length
    // Check for alphabetic characters
    // Must contain at least 1 alphabetic characters
    // Check for numeric case characters
    // Must contain at least 1 numeric case characters
    // Check for special characters
    // Must contain at least 1 characters from !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~
    if (isEmpty(this.state.newPassword) || isEmpty(this.state.confirmPassword)) {
      const message = this.props.intl.formatMessage(messages.missingPassword);
      Toast.error(message);
    } else if (this.state.newPassword.length < 8 && this.state.confirmPassword.length < 8) {
      const message = this.props.intl.formatMessage(messages.passwordLength);
      Toast.error(message);
    } else if (this.state.newPassword !== this.state.confirmPassword) {
      const message = this.props.intl.formatMessage(messages.mixMatch);
      Toast.error(message);
    } else if (
      !/.*(?=.*[a-zA-Z])(?=.*\d)(?=.*[\!\#\$\%\-\_\=\+\<\>\.]).*/.test(this.state.newPassword) ||
      !/.*(?=.*[a-zA-Z])(?=.*\d)(?=.*[\!\#\$\%\-\_\=\+\<\>\.]).*/.test(this.state.confirmPassword)
    ) {
      const message = this.props.intl.formatMessage(messages.passwordPolicyText);
      Toast.error(message);
    } else {
      if (!this.state.firstName) {
        const message = this.props.intl.formatMessage(messages.firstName);
        Toast.error(message);
      } else if (!this.state.lastName) {
        const message = this.props.intl.formatMessage(messages.lastName);
        Toast.error(message);
      } else {
        if (CxEngage.version) {
          this.setState({ loading: true });
          cxSubmitUserAccount(this.state.firstName, this.state.lastName, this.state.confirmPassword,
            (response) => {
              this.handleLoginResponse(response);
            });
        }  else {
          Toast.info(this.props.intl.formatMessage(messages.loadingSdk));
        }
      }
    }
  };

  setNewPassword = (e) => {
    this.setState({
      newPassword: e.target.value,
    });
  };

  setConfirmPassword = (e) => {
    this.setState({
      confirmPassword: e.target.value,
    });
  };

  setFirstName = (e) => {
    this.setState({
      firstName: e.target.value,
    });
  };

  setLastName = (e) => {
    this.setState({
      lastName: e.target.value,
    });
  };

  changeLocale = (locale) => {
    storage.setItem('locale', locale);
    this.props.changeLocale(locale);
  };

  getLoadingContent = () => (
    <div id="loginContainerDiv">
      <div style={styles.contentContainer}>
        <Loading size={120} />
      </div>
    </div>
  );

  render() {
    const userEmail = this.props.getUserEmail ? this.props.getUserEmail : 'your email address';
    if (!this.props.insideIframe && !this.props.hasStarted) {
      return (
        <Fragment>
          {this.state.redirect==true? <Redirect to="/" />:null }
          <LoginBox>
            <LoginFormWrapper>
              <Logo />
              {!this.state.loading && (
                <div>
                  <UserNameLabel>{'Username: ' + userEmail}</UserNameLabel>
                  {/* <PasswordPolicyText>
                  <FormattedMessage
                    id="inviteAccept.passwordPolicyText"
                    defaultMessage="Password must have at least 1 alphabetic character, 1 number, and 1 special character (! # $ % - _ = + < > .)"
                  />
                </PasswordPolicyText> */}
                  <TextInput
                    data-automation="newPassword"
                    placeholder={this.props.intl.formatMessage(messages.newPassword)}
                    type="password"
                    onChange={this.setNewPassword}
                    value={this.state.newPassword}
                  />
                  <TextInput
                    data-automation="confirmPassword"
                    placeholder={this.props.intl.formatMessage(messages.confirmPassword)}
                    type="password"
                    onChange={this.setConfirmPassword}
                    value={this.state.confirmPassword}
                  />
                  <TextInput
                    data-automation="firstName"
                    placeholder={this.props.intl.formatMessage(messages.firstName)}
                    type="text"
                    onChange={this.setFirstName}
                    value={this.state.firstName}
                  />
                  <TextInput
                    data-automation="lastName"
                    placeholder={this.props.intl.formatMessage(messages.lastName)}
                    type="text"
                    onChange={this.setLastName}
                    value={this.state.lastName}
                  />
                  <SubmitButton data-automation="submitUserAccountButton" onClick={() => this.submitUserAccount()}>
                    <FormattedMessage {...messages.submitUserAccount} />
                  </SubmitButton>
                </div>
              )}
              {this.state.loading && (
                this.getLoadingContent()
              )}
            </LoginFormWrapper>
          </LoginBox>
          {location.hash.includes('alpha') && (
            <StyledLanguagePicker
              color="#fff"
              onLanguageChange={this.changeLocale}
              selectedLocale={this.props.locale}
              languageOptions={mappedLocalesOptions}
            />
          )}
          <LegalPolicy />
          <PrivacyPolicy />
          <TempBackground />
        </Fragment>
      );
    } else if (!this.props.insideIframe && this.props.hasStarted) {
      return <Redirect to="/configuration/users" />;
    }
  }
}

InviteAccept.propTypes = {
  accountTenants: PropTypes.any,
  availableTenants: PropTypes.any,
  changeLocale: PropTypes.func.isRequired,
  fetchBranding: PropTypes.func.isRequired,
  getUserEmail: PropTypes.string,
  hasStarted: PropTypes.bool.isRequired,
  insideIframe: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  setAccountTenants: PropTypes.func.isRequired,
  setTenantsList: PropTypes.func.isRequired,
  setUserAuthed: PropTypes.func.isRequired,
  setUserTenants: PropTypes.func.isRequired,
  switchTenant: PropTypes.func.isRequired,
  updatePlatformPermissions: PropTypes.func,
  updateUserPermissions: PropTypes.func.isRequired,
};

export default injectIntl(InviteAccept);
